/*
* Copyright (c) 2014-2023 Inovalon, Inc. All Rights Reserved.
* This computer program is CONFIDENTIAL and a TRADE SECRET of Inovalon, Inc.
* The receipt or possession of this program does not convey any rights to use, reproduce or disclose
* its contents in whole or in part, without the specific written consent of Inovalon, Inc.
* Any use, reproduction or disclosure of this program without the express written consent of Inovalon, Inc.
* is a violation of the copyright laws and may subject you to criminal prosecution.
*/
import React, { Fragment } from 'react'
import './InpatientSummary.scss'
import { Collapse, CardBody, Card, CardHeader, Container } from 'reactstrap'
import { IServiceTypeElement, ISnfType, IBaseSection, ISummary, IInpatientTypeInsurance, IInpatientCoPayment, ICoverageDiscoveryResponseStateManager } from '../../../../services/CoverageDiscoveryResponseStateManager/contracts'
import { DescriptionsComponent } from '../../Medicare/Descriptions/Descriptions'
import { LimitationsComponent } from '../../Medicare/Limitations/Limitations'
import { LifetimeReserveComponent } from '../../Medicare/LifetimeReserve/LifetimeReserve'
import { CommonUtils } from '../../../../utils/CommonUtils'
import { Copays } from '../../Medicare/Copays/Copays'
import { CoInsurance } from '../../Medicare/CoInsurance/CoInsurance'

export interface IInpatientSummaryProps {
	CoverageDiscoveryResponseStateManager: ICoverageDiscoveryResponseStateManager
}

interface IInpatientSummaryState {
	collapse: number
}

export class InpatientSummaryComponent extends React.Component<IInpatientSummaryProps, IInpatientSummaryState> {
	private readonly dateFormat = 'MM/DD/YYYY'
	constructor(props: Readonly<IInpatientSummaryProps>) {
		super(props)
		this.toggle = this.toggle.bind(this)
		this.state = { collapse: 0 }
	}
	toggle(e): void {
		const event = e.target.dataset.event
		this.setState({ collapse: this.state.collapse === Number(event) ? 0 : Number(event) })
	}

	render(): React.ReactNode {
		const { collapse } = this.state
		const section = this.props.CoverageDiscoveryResponseStateManager.selectedMedicareInpatientSection
		if (!this.props.CoverageDiscoveryResponseStateManager.selectedMedicareInpatientSection) return <></>
		else
			return (
				<div id={section.id} className="accordion-group service-coverage" data-loadorder={section.loadOrder} data-index={section.summary.index}>
					<Card style={{ marginBottom: '1rem' }} className="card" key={section.displayOrder}>
						<CardHeader onClick={this.toggle} data-event={section.displayOrder} className="service-coverage accordion-heading whiteText">
							{section.summary.title}
						</CardHeader>
						<Collapse isOpen={collapse === section.displayOrder}>
							<CardBody className="card-body">{this.renderInpatientSection(section)}</CardBody>
						</Collapse>
					</Card>
				</div>
			)
	}
	renderInpatientSection(modelInpatient: IBaseSection) {
		const SelectedSections = modelInpatient.summary.selectedSections
		return (
			<Container style={{ margin: 0, padding: 0, width: '100%' }}>
				<div className="cdResponsePage">
					<table key={modelInpatient.summary.index} className="service-types-table">
						<tbody>
							<tr className="summaryDisplaying">
								<td colSpan={4}>
									Displaying {modelInpatient.summary.selectedSections.length} of {modelInpatient.summary.sections.length} sections
								</td>
							</tr>
						</tbody>
						<tbody className="summaryBody summaryBody0" style={{ display: SelectedSections.indexOf('0') !== -1 ? '' : 'none' }}>
							<tr className="summarySection">
								<td colSpan={4} className="cd-response-border">
									<h4>Inpatient Days Remaining</h4>
								</td>
							</tr>
						</tbody>
						{this.renderInpatientDaysRemaining(modelInpatient.summary, SelectedSections)}
						<tbody className="summaryBody summaryBody1" style={{ display: SelectedSections.indexOf('1') !== -1 ? '' : 'none' }}>
							<tr className="summarySection cd-response-border">
								<td colSpan={2}>
									<h4>SNF Days Remaining</h4>
								</td>
								<td className="summaryHide" colSpan={2}>
									<input type="hidden" value="1" />
								</td>
							</tr>
						</tbody>
						<tbody className="summaryBody summaryBody1" style={{ display: SelectedSections.indexOf('1') !== -1 ? '' : 'none' }}>
							{this.renderSNFDaysRemaining(modelInpatient.summary)}
						</tbody>
						<tbody className="summaryBody summaryBody2" style={{ display: SelectedSections.indexOf('2') !== -1 ? '' : 'none' }}>
							<tr className="summarySection">
								<td colSpan={2}>
									<h4>End Stage Renal Disease</h4>
								</td>
								<td className="summaryHide" colSpan={2}>
									<input type="hidden" value="2" />
								</td>
							</tr>
						</tbody>
						<tbody className="summaryBody summaryBody2" style={{ display: SelectedSections.indexOf('2') !== -1 ? '' : 'none' }}>
							{this.renderESRD(modelInpatient.summary)}
						</tbody>
					</table>
				</div>
			</Container>
		)
	}
	renderInpatientDaysRemaining(modelInpatient: ISummary, SelectedSections: string[]) {
		let noSpellsFoundContent: JSX.Element | null = null
		let inpatientTypesContent: JSX.Element[] | null = null

		if (modelInpatient.inpatientCoPayments === undefined || modelInpatient.inpatientCoPayments.length === 0 || !this.contaisEpisodes(modelInpatient.inpatientCoPayments)) {
			noSpellsFoundContent = (
				<tbody className="summaryBody summaryBody0" style={{ display: SelectedSections.indexOf('0') !== -1 ? '' : 'none' }}>
					<tr className="cd-response-border">
						<td>&nbsp;</td>
						<td colSpan={3} className="bottom-border-td">
							No spells found within the requested service dates
						</td>
					</tr>
				</tbody>
			)
		}
		const inpatientCodes: string[] = []
		const inpatientTypes: IInpatientTypeInsurance[] | undefined | null = modelInpatient.inpatientTypes && this.getInpatientTypes(modelInpatient.inpatientTypes)

		if (inpatientTypes && inpatientTypes.length > 0) {
			inpatientTypesContent = inpatientTypes
				.sort((first, second) => CommonUtils.compareProperty(first.code, second.code))
				.map((iType, index) => {
					let typeCodeFound = false
					if (iType.code) {
						typeCodeFound = inpatientCodes.includes(iType.code)
						inpatientCodes.push(iType.code)
					}

					return (
						<Fragment key={index}>
							<tbody className="summaryBody summaryBody0" style={{ display: SelectedSections.indexOf('0') !== -1 ? '' : 'none' }}>
								{!typeCodeFound ? (
									<tr className="sub-header topline cd-response-border">
										<td>Insurance Type:</td>
										<td colSpan={3}>{iType.definition}</td>
									</tr>
								) : null}

								<Fragment key={index + 'a'}>
									<Copays data={iType} />
									<LifetimeReserveComponent data={iType} />
									<CoInsurance data={iType} />
									<DescriptionsComponent data={iType} />
									<LimitationsComponent data={iType} />
								</Fragment>
							</tbody>
						</Fragment>
					)
				})
		}

		return (
			<>
				{noSpellsFoundContent}
				{inpatientTypesContent}
			</>
		)
	}
	getInpatientTypes(inpatientTypes: IServiceTypeElement[]): IInpatientTypeInsurance[] {
		const inpatientTypesResult: IInpatientTypeInsurance[] = []

		if (inpatientTypes && inpatientTypes.length >= 0) {
			const combinedInsuranes = Array.from(inpatientTypes, p => p.insurances)
			const tempInsurances: IInpatientTypeInsurance[] = []
			for (const insurances of combinedInsuranes) {
				insurances.forEach(ins => {
					tempInsurances.push(ins)
				})
			}

			if (tempInsurances.length > 0) {
				tempInsurances.forEach(insurance => {
					if (insurance.code === 'MA' || insurance.code === 'QM') {
						inpatientTypesResult.push(insurance)
					}
				})
			}
		}

		return inpatientTypesResult
	}
	contaisEpisodes(inpatientCoPayments?: IInpatientCoPayment[]): boolean {
		if (!inpatientCoPayments) {
			return false
		}
		let valueFound = false
		for (let index = 0; index < inpatientCoPayments.length; index++) {
			const item: IInpatientCoPayment = inpatientCoPayments[index]
			if (item.episodes !== undefined && item.episodes.length > 0) {
				valueFound = true
				break
			}
		}
		return valueFound
	}

	renderSNFDaysRemaining(modelInpatient: ISummary) {
		let noSpellsFoundContent: JSX.Element | null = null
		let snfDaysRemainingContent: JSX.Element[] | null = null

		if (modelInpatient.inpatientCoPayments === undefined || modelInpatient.inpatientCoPayments.length === 0 || !this.contaisEpisodes(modelInpatient.inpatientCoPayments)) {
			noSpellsFoundContent = (
				<tr className="cd-response-border">
					<td>&nbsp;</td>
					<td colSpan={3} className="bottom-border-td">
						No spells found within the requested service dates
					</td>
				</tr>
			)
		}
		const snfCodes: string[] = []
		const snfTypes: IInpatientTypeInsurance[] | undefined = modelInpatient.snfTypes && this.getSnfTypes(modelInpatient.snfTypes)

		if (snfTypes && snfTypes.length > 0) {
			snfDaysRemainingContent = snfTypes
				.sort((first, second) => CommonUtils.compareProperty(first.code, second.code))
				.map((iType, index) => {
					let typeCodeFound = false
					if (iType.code) {
						typeCodeFound = snfCodes.includes(iType.code)
						snfCodes.push(iType.code)
					}

					return (
						<Fragment key={index}>
							{!typeCodeFound ? (
								<tr className="sub-header topline">
									<td>Insurance Type:</td>
									<td colSpan={3}>{iType.definition}</td>
								</tr>
							) : null}

							<Fragment>
								<Copays data={iType} />
								<LifetimeReserveComponent data={iType} />
								<CoInsurance data={iType} />
								<DescriptionsComponent data={iType} />
								<LimitationsComponent data={iType} />
							</Fragment>
						</Fragment>
					)
				})
		}

		return (
			<>
				{noSpellsFoundContent}
				{snfDaysRemainingContent}
			</>
		)
	}

	getSnfTypes(snfTypes: ISnfType[]): IInpatientTypeInsurance[] {
		const snfTypesResult: IInpatientTypeInsurance[] = []

		if (snfTypes !== undefined && snfTypes.length >= 0) {
			snfTypes.forEach(item => {
				item.insurances.forEach(ins => {
					if (ins.code === 'MA' || ins.code === 'QM') {
						snfTypesResult.push(ins)
					}
				})
			})
		}

		return snfTypesResult
	}

	renderESRD(summary: ISummary) {
		const showEsrd = !CommonUtils.isMinDate(summary.esrdEffectiveDate) || !CommonUtils.isMinDate(summary.esrdEndDate)
		const showEsrdDialysis = !CommonUtils.isMinDate(summary.esrdDialysisEffectiveDate) || !CommonUtils.isMinDate(summary.esrdDialysisEndDate)
		const showEsrdTransplant = !CommonUtils.isMinDate(summary.esrdTransplantEffectiveDate) || !CommonUtils.isMinDate(summary.esrdTransplantEndDate)

		const esrdSection = (
			<tr className="bottom-border-td">
				<td>ESRD Effective Date:</td>
				<td>{CommonUtils.getDate(summary.esrdEffectiveDate, this.dateFormat)}</td>
				<td>ESRD End Date:</td>
				<td>{CommonUtils.getDate(summary.esrdEndDate, this.dateFormat)}</td>
			</tr>
		)

		const esrdDialysisSection = (
			<tr className="bottom-border-td">
				<td>Dialysis Effective Date:</td>
				<td>{CommonUtils.getDate(summary.esrdDialysisEffectiveDate, this.dateFormat)}</td>
				<td>Dialysis End Date:</td>
				<td>{CommonUtils.getDate(summary.esrdDialysisEndDate, this.dateFormat)}</td>
			</tr>
		)

		const esrdTransplantSection = (
			<tr className="bottom-border-td">
				<td>Transplant Effective Date:</td>
				<td>{CommonUtils.getDate(summary.esrdTransplantEffectiveDate, this.dateFormat)}</td>
				<td>Transplant End Date:</td>
				<td>{CommonUtils.getDate(summary.esrdTransplantEndDate, this.dateFormat)}</td>
			</tr>
		)

		const esrdEmptySection = (
			<tr className="no-info">
				<td colSpan={4} className="bottom-border-td">
					No End Stage Renal Disease information available
				</td>
			</tr>
		)

		const esrdRenderSection = (
			<Fragment>
				{showEsrd && esrdSection}
				{showEsrdDialysis && esrdDialysisSection}
				{showEsrdTransplant && esrdTransplantSection}
				{!showEsrd && !showEsrdDialysis && !showEsrdTransplant && esrdEmptySection}
			</Fragment>
		)

		return esrdRenderSection
	}
}
